import { Button } from '@ctw/shared/components/button';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import {
  TelemetryBoundary,
  type TelemetryContext,
} from '@ctw/shared/context/telemetry/telemetry-boundary';
import type { TelemetryUserInteraction } from '@ctw/shared/context/telemetry/user-interactions';
import { type ClassName, tw, twx } from '@ctw/shared/utils/tailwind';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { uniq } from 'lodash-es';
import type { PropsWithChildren } from 'react';
import { Fragment } from 'react';

export type PaginationProps = {
  currentPage: number;
  isLoading?: boolean;
  pageSize: number;
  setCurrentPage: (n: number) => void;
  total: number;
};

export const Pagination = ({
  currentPage,
  isLoading = false,
  pageSize,
  setCurrentPage,
  total,
}: PaginationProps) => {
  const start = Math.min(total, (currentPage - 1) * pageSize + 1);
  const finish = Math.min(currentPage * pageSize, total);
  const pageCount = Math.ceil(total / pageSize);
  const prevPage = Math.max(currentPage - 1, 1);
  const nextPage = Math.min(currentPage + 1, pageCount);

  // Always show the first page link and the last.
  let pagesToShow = [1, pageCount];
  // Always show currentPage and one before and after.
  pagesToShow.push(currentPage - 1, currentPage, currentPage + 1);
  // Dedupe, sort and filter away any pages that are out of range.
  pagesToShow = uniq(
    pagesToShow.sort((a, b) => a - b).filter((page) => page >= 1 && page <= pageCount),
  );

  return (
    <TelemetryBoundary
      boundaryName="PaginationByPage"
      initialContext={{
        paginationTotal: total,
        paginationCurrentPage: currentPage,
        paginationPageSize: pageSize,
      }}
    >
      {() => (
        <div className={tw`flex justify-between px-6 py-3`}>
          <div className={tw`text-background-tooltip text-sm`}>
            {isLoading ? (
              <LoadingSpinner message="Loading..." />
            ) : (
              <>
                <span className={tw`font-medium`}>{start}</span> to{' '}
                <span className={tw`font-medium`}>{finish}</span> of{' '}
                <span className={tw`font-medium`}>{total}</span> results
              </>
            )}
          </div>

          {pageCount > 1 && (
            <div>
              <nav
                className={tw`relative z-0 inline-flex -space-x-px rounded-md shadow-sm`}
                aria-label="Pagination"
              >
                <PaginationButton
                  telemetryInteractionName="pagination_previous"
                  telemetryContext={{
                    paginatedFromPage: currentPage,
                    paginatedToPage: prevPage,
                  }}
                  onClick={() => setCurrentPage(prevPage)}
                  active={false}
                  className={tw`rounded-l-md hover:bg-background-hover`}
                  disabled={currentPage === 1}
                >
                  <span className={tw`sr-only`}>Previous</span>
                  <FontAwesomeIcon
                    icon={faChevronLeft}
                    className={tw`h-5 w-5`}
                    aria-hidden="true"
                  />
                </PaginationButton>

                {pagesToShow.map((page, index) => {
                  const prev = pagesToShow[index - 1] ?? 0;
                  const pagesSkipped = page - prev - 1;

                  return (
                    <Fragment key={page}>
                      {pagesSkipped > 1 && (
                        <span
                          className={tw`relative inline-flex items-center border border-content-subtle border-b border-solid bg-white px-4 py-2 font-medium text-content-subtle text-sm`}
                        >
                          ...
                        </span>
                      )}
                      {pagesSkipped === 1 && (
                        <PaginationButton
                          telemetryInteractionName="pagination_jump_to"
                          telemetryContext={{
                            paginatedFromPage: currentPage,
                            paginatedToPage: page - 1,
                          }}
                          onClick={() => setCurrentPage(page - 1)}
                          active={currentPage === page - 1}
                          disabled={false}
                        >
                          {page - 1}
                        </PaginationButton>
                      )}

                      <PaginationButton
                        telemetryInteractionName="pagination_jump_to"
                        telemetryContext={{
                          paginatedFromPage: currentPage,
                          paginatedToPage: page,
                        }}
                        onClick={() => setCurrentPage(page)}
                        active={currentPage === page}
                        disabled={false}
                      >
                        {page}
                      </PaginationButton>
                    </Fragment>
                  );
                })}

                <PaginationButton
                  className={tw`rounded-r-md`}
                  telemetryInteractionName="pagination_next"
                  telemetryContext={{
                    paginatedFromPage: currentPage,
                    paginatedToPage: nextPage,
                  }}
                  onClick={() => setCurrentPage(nextPage)}
                  active={false}
                  disabled={currentPage === pageCount}
                >
                  <span className={tw`sr-only`}>Next</span>
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    className={tw`h-5 w-5`}
                    aria-hidden="true"
                  />
                </PaginationButton>
              </nav>
            </div>
          )}
        </div>
      )}
    </TelemetryBoundary>
  );
};

interface PaginationButtonProps extends PropsWithChildren {
  className?: ClassName;
  disabled: boolean;
  active: boolean;
  onClick: () => void;
  telemetryInteractionName: TelemetryUserInteraction;
  telemetryContext: TelemetryContext;
}

const PaginationButton = ({
  children,
  className,
  onClick,
  active,
  disabled,
  telemetryInteractionName,
  telemetryContext,
}: PaginationButtonProps) => {
  return (
    <Button
      type="button"
      variant="unstyled"
      disabled={disabled}
      onClick={onClick}
      className={twx(
        className,
        'relative inline-flex items-center border border-content-subtle border-b border-solid bg-white px-2 py-2 font-medium text-content-subtle text-sm',
        {
          'z-10 border-primary-main bg-primary-background-hover text-primary-text': active,
          'cursor-pointer hover:bg-divider-main': !(disabled || active),
        },
      )}
      telemetryInteractionName={telemetryInteractionName}
      telemetryContext={telemetryContext}
    >
      {children}
    </Button>
  );
};
